import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { formatDateTime, formatNumber } from '@/services/format';
import type { ClientDetail, UsageActionEntry, PagedResult } from '@/types';
import { UsageActionKind } from '@/types';

const KIND_LABEL: Record<UsageActionKind, string> = {
  [UsageActionKind.Used]: 'Registo',
  [UsageActionKind.Consume]: 'Débito',
  [UsageActionKind.Revert]: 'Reembolso',
  [UsageActionKind.Grant]: 'Atribuição',
};

const KIND_BADGE: Record<UsageActionKind, string> = {
  [UsageActionKind.Used]: 'bg-gray-100 text-gray-700',
  [UsageActionKind.Consume]: 'bg-magni-blue-50 text-magni-blue-700',
  [UsageActionKind.Revert]: 'bg-magni-green-50 text-magni-green-700',
  [UsageActionKind.Grant]: 'bg-magni-green-50 text-magni-green-700',
};

export function ClientDetailPage() {
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const clientId = Number(clientIdParam);

  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [events, setEvents] = useState<PagedResult<UsageActionEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    if (!Number.isFinite(clientId)) return;
    setLoading(true);
    (async () => {
      try {
        const [d, e] = await Promise.all([
          adminApi.clientDetail(clientId),
          adminApi.usageActions({ clientId, page: 1, pageSize }),
        ]);
        setDetail(d);
        setEvents(e);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível carregar o cliente');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const loadEventsPage = async (page: number) => {
    if (!Number.isFinite(clientId)) return;
    setEventsLoading(true);
    try {
      setEvents(await adminApi.usageActions({ clientId, page, pageSize }));
      setEventsPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os eventos');
    } finally {
      setEventsLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">A carregar…</div>;
  if (error) return <div className="p-8 text-magni-red-600">{error}</div>;
  if (!detail) return (
    <div className="p-8 max-w-[1280px] mx-auto">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-magni-red hover:underline mb-3">
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        Clientes
      </Link>
      <h1 className="page-h1 font-mono mb-8">{clientId}</h1>
      <p className="text-gray-500">Este cliente ainda não tem utilização registada.</p>
    </div>
  );

  const actionMax = Math.max(1, ...detail.byAction.map(a => a.totalQuantity));
  const featureMax = Math.max(1, ...detail.byFeature.map(f => f.totalQuantity));
  const totalEventsPages = events ? Math.max(1, Math.ceil(events.total / events.pageSize)) : 1;

  return (
    <div className="p-8 max-w-[1280px] mx-auto">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-magni-red hover:underline mb-3">
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        Clientes
      </Link>
      <h1 className="page-h1 font-mono mb-8">{detail.clientId}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
        <Tile label="Quantidade total" value={formatNumber(detail.totalQuantity)} />
        <Tile label="Eventos" value={formatNumber(detail.eventCount)} />
        <Tile label="Ações distintas" value={formatNumber(detail.actionCount)} />
        <Tile label="Funcionalidades" value={formatNumber(detail.featureCount)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 text-sm text-gray-600">
        <div className="card">
          <p className="eyebrow mb-2">Primeira utilização</p>
          <p className="text-gray-900">{formatDateTime(detail.firstSeenAt)}</p>
        </div>
        <div className="card">
          <p className="eyebrow mb-2">Última utilização</p>
          <p className="text-gray-900">{formatDateTime(detail.lastSeenAt)}</p>
        </div>
      </div>

      <Section title={`Por ação (${detail.byAction.length})`}>
        {detail.byAction.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Ação</th>
                <th className="px-6 py-3 font-semibold text-right">Registos</th>
                <th className="px-6 py-3 font-semibold text-right">Débitos</th>
                <th className="px-6 py-3 font-semibold text-right">Reembolsos</th>
                <th className="px-6 py-3 font-semibold">Última</th>
                <th className="px-6 py-3 font-semibold text-right w-1/4">Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {detail.byAction.map(a => (
                <tr key={a.actionId} className="border-t border-gray-100">
                  <td className="px-6 py-3 font-mono text-xs text-gray-800">
                    {a.actionToken || <span className="text-gray-400">#{a.actionId}</span>}
                  </td>
                  <td className="px-6 py-3 text-right text-gray-700">{formatNumber(a.usedCount)}</td>
                  <td className="px-6 py-3 text-right text-magni-blue-700">{formatNumber(a.consumeCount)}</td>
                  <td className="px-6 py-3 text-right text-magni-green-700">{formatNumber(a.revertCount)}</td>
                  <td className="px-6 py-3 text-xs text-gray-700">{formatDateTime(a.lastAt)}</td>
                  <td className="px-6 py-3 text-right"><Bar value={a.totalQuantity} max={actionMax} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={`Por funcionalidade (${detail.byFeature.length})`}>
        {detail.byFeature.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Funcionalidade</th>
                <th className="px-6 py-3 font-semibold text-right">Eventos</th>
                <th className="px-6 py-3 font-semibold text-right w-2/3">Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {detail.byFeature.map(f => (
                <tr key={f.magniFeatureId} className="border-t border-gray-100">
                  <td className="px-6 py-3 font-mono text-xs text-gray-800">
                    {f.featureToken || <span className="text-gray-400">#{f.magniFeatureId}</span>}
                  </td>
                  <td className="px-6 py-3 text-right text-gray-700">{formatNumber(f.eventCount)}</td>
                  <td className="px-6 py-3 text-right"><Bar value={f.totalQuantity} max={featureMax} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={`Eventos${events ? ` (${formatNumber(events.total)})` : ''}`}>
        {!events || events.items.length === 0 ? <Empty /> : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-6 py-3 font-semibold">Quando</th>
                  <th className="px-6 py-3 font-semibold">Tipo</th>
                  <th className="px-6 py-3 font-semibold">Utilizador</th>
                  <th className="px-6 py-3 font-semibold">Funcionalidade</th>
                  <th className="px-6 py-3 font-semibold">Ação</th>
                  <th className="px-6 py-3 font-semibold">Operador</th>
                  <th className="px-6 py-3 font-semibold text-right">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {events.items.map(u => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="px-6 py-3 text-xs text-gray-700">{formatDateTime(u.at)}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${KIND_BADGE[u.kind] ?? 'bg-gray-100 text-gray-700'}`}>
                        {KIND_LABEL[u.kind] ?? `Kind ${u.kind}`}
                      </span>
                      {u.kind === UsageActionKind.Consume && u.revertedAt && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-50 text-gray-500 line-through">revertido</span>
                      )}
                    </td>
                    <td className="px-6 py-3 font-mono text-gray-700">{u.userId ?? <span className="text-gray-400">—</span>}</td>
                    <td className="px-6 py-3 font-mono text-xs text-gray-700">{u.featureToken ?? '—'}</td>
                    <td className="px-6 py-3 font-mono text-xs text-gray-700">{u.actionToken ?? <span className="text-gray-400">#{u.actionId}</span>}</td>
                    <td className="px-6 py-3 text-xs text-gray-700">{u.performedByEmail ?? <span className="text-gray-400">—</span>}</td>
                    <td className="px-6 py-3 text-right text-gray-800">{formatNumber(u.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-3 flex items-center justify-between text-sm text-gray-600 border-t border-gray-100">
              <p>Página {events.page} de {totalEventsPages}{eventsLoading ? ' · a carregar…' : ''}</p>
              <div className="flex gap-2">
                <button disabled={eventsPage <= 1 || eventsLoading} onClick={() => loadEventsPage(eventsPage - 1)}
                  className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">Anterior</button>
                <button disabled={eventsPage >= totalEventsPages || eventsLoading} onClick={() => loadEventsPage(eventsPage + 1)}
                  className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">Seguinte</button>
              </div>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <p className="eyebrow mb-2">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="eyebrow mb-3">{title}</h2>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">{children}</div>
    </section>
  );
}

function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center justify-end gap-3">
      <span className="text-gray-800 tabular-nums">{formatNumber(value)}</span>
      <div className="w-32 h-2 bg-gray-100 rounded-pill overflow-hidden">
        <div className="h-2 bg-gray-500 rounded-pill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Empty() {
  return <p className="px-6 py-6 text-center text-gray-500">Sem dados.</p>;
}
